/**
 * Created by Abner Sui on 05/08/2020
 * ------ maintenance history ------
 * 11/28/2022 Simon Zhao add the support of the display of history update records of done tasks.
 */

import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { ProcessInstance, TaskType, TaskStatus, HistoryRecord, ApprovalTask, Field, TaskDefinition, ProjectHistoryItem, ProjectHistoryActionType, FieldDefinition, ProjectHistory, FieldType, FileInfo } from '../../../tamalelibs/models/workflow.model';
import { EntityBrief } from '../../../tamalelibs/models/entity-brief.model';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { Entity } from '../../../tamalelibs/models/entity.model';
import { KeyValue } from '@angular/common';
import { CheckListItemModel } from '../field/check-list-config/check-list-config.component';
import { Subscription } from 'rxjs';
import { ProjectHistoryFieldDetail, ProjectHistoryItemViewModel } from './instance-task-timeline.model';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';

@Component({
    // tslint:disable-next-line: component-selector
    selector: 'instance-task-timeline',
    templateUrl: './instance-task-timeline.component.html',
    styleUrls: ['./instance-task-timeline.component.scss']
})
export class InstanceTaskTimelineComponent implements OnInit, OnDestroy {

    @Input() instance: ProcessInstance;

    isLoading = false;

    records: Array<ProjectHistoryItemViewModel> = [];

    private _destroySubscriptions: Array<Subscription> = [];

    private _projectHistory: ProjectHistory;
    constructor(
        private _workflowService: WorkflowService,
    ) { }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => {
            if (subscription) {
                subscription.unsubscribe();
            }
        });
    }

    ngOnInit() {
        this.isLoading = true;
        if (this.instance) {
            this._destroySubscriptions.push(
                this._workflowService.getProjectHistory(this.instance.id).subscribe(res => {
                    // keep automation tasks from displaying so far.
                    const actions_to_ignore = ['trigger'];
                    res.items = res.items.filter(it => !actions_to_ignore.includes(it.action));
                    this._projectHistory = res;
                    res.items.forEach(rawItem => {
                        const itemVM = new ProjectHistoryItemViewModel();
                        itemVM.record = rawItem;
                        let fieldsArray: Array<KeyValue<string, any>>;
                        if (rawItem.data.fields && rawItem.data.fields.length > 0) {
                            fieldsArray = rawItem.data.fields;
                        } else if (rawItem.data.updateFields && rawItem.data.updateFields.length > 0) {
                            fieldsArray = rawItem.data.updateFields;
                        }

                        if (fieldsArray && fieldsArray.length) {
                            fieldsArray.forEach(rawFld => {
                                const fldVm = new ProjectHistoryFieldDetail();
                                const fldDef = this._projectHistory.fieldDefinitions.find(fld => fld.id === rawFld.key);
                                fldVm.fieldDefinition = fldDef;
                                if (fldDef.type === FieldType.ENTITY || fldDef.type === FieldType.User) {
                                    fldVm.value = Array.from(rawFld.value).map(rawEntity =>
                                        Entity.parse(rawEntity['data'])
                                    );
                                } else if (fldDef.type === FieldType.FILE && rawFld.value && Array.isArray(rawFld.value)) {
                                    fldVm.value = Array.from(rawFld.value).map(fileIt => {
                                        const flInfo = FileInfo.parse(fileIt['data']);
                                        if (!flInfo.taskId && itemVM.record.data.taskID) {
                                            flInfo.taskId = itemVM.record.data.taskID;
                                        }
                                        return flInfo;
                                    });
                                } else {
                                    fldVm.value = rawFld.value;
                                }

                                itemVM.fieldsArray.push({ key: rawFld.key, value: fldVm });
                            });
                        }
                        this.records.push(itemVM);
                    });
                    this.records.sort((a, b) => a.record.time.getTime() - b.record.time.getTime());
                    this.isLoading = false;
                }, error => this.isLoading = false)
            );
        }
    }

    getActionName(item: ProjectHistoryItem): string {
        let result = '';
        switch (item.action) {
            case ProjectHistoryActionType.REJECT:
                result = StringLiteralsPipe.translate('workflow.task_status_rejected').toLowerCase();
                break;
            case ProjectHistoryActionType.COMPLETE:
                result = StringLiteralsPipe.translate('workflow.task_status_completed').toLowerCase();
                break;
            case ProjectHistoryActionType.APPROVE:
                result = StringLiteralsPipe.translate('workflow.task_status_approved').toLowerCase();
                break;
            case ProjectHistoryActionType.COMPLETE_UPDATE:
                result = StringLiteralsPipe.translate('workflow.task_status_updated').toLowerCase();
                break;
            case ProjectHistoryActionType.SENDBACK:
                result = StringLiteralsPipe.translate('workflow.task_status_sentback').toLowerCase();
                break;
            default:
                break;
        }
        return result;
    }

    getFieldDef(fldID: string): FieldDefinition {
        return this._projectHistory.fieldDefinitions.find(fld => fld.id === fldID);
    }

    getDisplayValueForEntities(entities: Array<EntityBrief>): string {
        return this._workflowService.getDisplayValueForEntities(entities);
    }

    getFormatForNumber(field: ProjectHistoryFieldDetail) {
        const fieldModel = new Field();
        fieldModel.id = field.fieldDefinition.id;
        fieldModel.fieldDefinition = field.fieldDefinition;
        fieldModel.value = field.value;
        return this._workflowService.getFormatForNumber(fieldModel, this._projectHistory.templates && this._projectHistory.templates.length > 0 ? this._projectHistory.templates[0] : null);
    }

    /**
     * gets the description array of checked items.
     * @param fieldKV the check list field's key value pair.
     * @param taskDef the definition of the task, to which the given field belong.
     * @returns a string array, each of them is the discription of a checked item.
     */
    getCheckedItems(fieldDetail: ProjectHistoryFieldDetail) {
        const checkedItemDescriptions = [];
        // the function extract the control config related to the given field from the task's template.
        const getFieldInTemplate = (fld: string, templates: Array<any>) => {
            if (templates && templates.length > 0) {
                for (let tI = 0; tI < templates.length; tI++) {
                    const template = templates[tI];
                    if (template && template.contains) {
                        const rows: Array<Array<any>> = Array.from(template.contains);
                        for (let iRow = 0; iRow < rows.length; iRow++) {
                            const row: Array<any> = rows[iRow];
                            const targetCtrl = row.find(ctrl => ctrl.fieldDefinitionId === fld);
                            if (targetCtrl) {
                                return targetCtrl;
                            }
                        }
                    }
                }
            }
            return null;
        };
        if (fieldDetail && fieldDetail.value && this._getTaskTemplates()) {
            // obtains the field's corresponding control, in order to querying the check items' config.
            const fieldCtrl = getFieldInTemplate(fieldDetail.fieldDefinition.id, this._getTaskTemplates());
            // the status of the check list field.
            const checkedStatus = fieldDetail.value;
            if (fieldCtrl) {
                const itemsConfig: Array<CheckListItemModel> = fieldCtrl.checkItems;
                // iterates the check list and collect the description of the checked ones.
                Object.keys(checkedStatus).forEach(
                    k => {
                        if (checkedStatus[k]) {
                            // the item is checked, find corresponding config to get the description.
                            const checkedItemCfg = itemsConfig.find(cfg => cfg.index.toString() === k);
                            if (checkedItemCfg) {
                                checkedItemDescriptions.push(checkedItemCfg.description);
                            }
                        }
                    }
                );
            }
        }
        return checkedItemDescriptions;
    }

    /**
     * obtains the relative url to zoom the corresponding entity.
     * @param v the entity field value
     * @returns a relative url to zoom the corresponding entity.
     */
    getEntityRelativeLink(v: Entity) {
        return WorkflowService.getEntityRelativeLink(v);
    }

    isDetailAreaVisible(item: ProjectHistoryItem): boolean {
        return !!item.data && !!item.data.taskName;
    }

    /**
     * determines whether the given value is an array.
     * @param v the given field value.
     * @returns a flag indicating whether the given value is an array.
     */
    isEntityArray(v: Array<Entity> | Entity) {
        return Array.isArray(v);
    }

    /**
     * determines whether the entity container should wrap by the length of entity value array.
     * @param entities the entities value array
     * @returns a flag indicating whether the entity container should wrap
     */
    isEntityDivWrap(entities: Array<any>) {
        return !!entities && entities.length === 1;
    }

    /**
     * Determine whether a given history item includes target task part.
     * @param item the corresponding history item.
     * @returns A flag indicating whether the given history item should display target task part.
     */
    isTargetContentVisible(item: ProjectHistoryItemViewModel) {
        return item && !!item.record && !!item.record.data && !!item.record.data.targetTaskName;
    }

    onClickUser(event, item: ProjectHistoryItem): void {
        if (item.user) {
            window.open('contact/' + item.user.id, '_blank');
        }
    }

    trackByFn(index) {
        return index;
    }

    private _getTaskTemplates() {
        return this._projectHistory.templates && this._projectHistory.templates.length > 0 ? this._projectHistory.templates : null;
    }
}
